import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import PlayerCard from "./PlayerCard";
import SourceViewer from "./SourceViewer";
import { BookGlyph, useElementSize } from "../player/controls";
import { trackEvent } from "../../analytics";

/*
 * Class media row (player redesign, 2026-10 — "source-text split"). The
 * player owns the full main column; when the class has a source PDF the
 * player bar gets a 📖 Source toggle that splits the row 50/50 with a
 * SourceViewer locked to the player card's height. Below SPLIT_MIN_WIDTH
 * (phones) the viewer stacks under the player and fills the rest of the
 * screen (viewport − header − player, min 320px), and opening it scrolls the
 * player up under the header, so header → player → text is one screen
 * (mobile media-focus brief). Whether the
 * source is open is remembered per device, but only ever applies to classes
 * that have one. Classes without a source get the one-line NoSourceNote.
 */

const PREF_KEY = "ic-source-open";
const SPLIT_MIN_WIDTH = 760;
const STACK_GAP = 12; // gap-3 between player and pane
const STACK_MARGIN = 12; // breathing room under the pane

const headerHeight = () => parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--ic-header-h")) || 0;

const readPref = () => {
  try {
    return localStorage.getItem(PREF_KEY) === "1";
  } catch (_) {
    return false;
  }
};
const writePref = (open) => {
  try {
    localStorage.setItem(PREF_KEY, open ? "1" : "0");
  } catch (_) {}
};

export function NoSourceNote({ seriesInfo }) {
  return (
    <p className="m-0 flex items-start gap-2 text-[14px] leading-5 text-ic-slate">
      <span className="flex-shrink-0 mt-px">
        <BookGlyph size={18} />
      </span>
      <span>
        No source text — this shiur is taught freely, without a written text.
        {seriesInfo && (
          <>
            {" "}
            <Link to={`/series/${seriesInfo.permalink || seriesInfo.series_id}`} className="font-semibold text-ic-blue hover:underline">
              Browse series resources
            </Link>
          </>
        )}
      </span>
    </p>
  );
}

export default function ClassMedia({ classData, seriesInfo }) {
  const file = classData.files[0];
  const [pref, setPref] = useState(readPref);
  // The remembered preference never opens a pane for a class without a source.
  const open = Boolean(file) && pref;
  // Mount the viewer on first open and keep it (hidden) after closing, so the
  // PDF is fetched only when someone actually opens it, and only once.
  const [mounted, setMounted] = useState(open);
  useEffect(() => {
    if (open) setMounted(true);
  }, [open]);
  // A different class: don't keep (or fetch) its PDF unless the pane is open.
  useEffect(() => {
    setMounted(open);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file?.url]);

  const rowRef = useRef(null);
  const playerRef = useRef(null);
  const { width: rowWidth } = useElementSize(rowRef);
  const { height: playerHeight } = useElementSize(playerRef);
  const stacked = rowWidth > 0 && rowWidth < SPLIT_MIN_WIDTH;

  const toggle = () => {
    const next = !open;
    setPref(next);
    writePref(next);
    if (next && stacked && playerRef.current) {
      // Bring the player up under the (sticky) header so the pane below it fits the screen.
      const top = playerRef.current.getBoundingClientRect().top + window.scrollY - headerHeight() - 8;
      const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
      requestAnimationFrame(() => window.scrollTo({ top: Math.max(0, top), behavior: reduce ? "auto" : "smooth" }));
    }
    try {
      trackEvent({ category: "player", action: next ? "source_open" : "source_close", label: classData.title });
    } catch (_) {}
  };

  // The same wrappers render with or without a source, so the size
  // measurements stay attached when navigation swaps one class for another.
  const split = Boolean(file) && !stacked;
  return (
    <div ref={rowRef} className={split ? "flex items-start gap-5" : "flex flex-col gap-3"}>
      <div
        ref={playerRef}
        className="min-w-0 transition-[flex-basis] duration-150 ease-out"
        style={split ? { flex: open ? "1 1 0" : "1 1 100%" } : undefined}
      >
        <PlayerCard classData={classData} seriesInfo={seriesInfo} sourceToggle={file ? { open, onToggle: toggle } : null} />
      </div>
      {!file && <NoSourceNote seriesInfo={seriesInfo} />}
      {file && mounted && (
        <div className={open ? "min-w-0" : "hidden"} style={split ? { flex: "1 1 0" } : undefined}>
          <SourceViewer
            file={file}
            height={
              stacked
                ? `max(320px, calc(100dvh - var(--ic-header-h, 0px) - ${Math.round(playerHeight) + STACK_GAP + STACK_MARGIN + 8}px))`
                : playerHeight
            }
            onClose={toggle}
          />
        </div>
      )}
    </div>
  );
}
